import React, { useEffect, useState, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import Map, { Marker, NavigationControl, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import Supercluster from 'supercluster';

// Helper to create a GeoJSON circle
const createGeoJSONCircle = (center, radiusInKm, points = 64) => {
    const coords = {
        latitude: center[1],
        longitude: center[0]
    };

    const km = radiusInKm;

    const ret = [];
    const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    const distanceY = km / 110.574;

    let theta, x, y;
    for (let i = 0; i < points; i++) {
        theta = (i / points) * (2 * Math.PI);
        x = distanceX * Math.cos(theta);
        y = distanceY * Math.sin(theta);

        ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    return {
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [ret]
        },
        properties: {}
    };
};

const TripMap = forwardRef(({ trip, activePhotoId, onMarkerClick }, ref) => {
    const [viewState, setViewState] = useState({
        longitude: -156.3319,
        latitude: 20.7984,
        zoom: 10
    });
    const [clusters, setClusters] = useState([]);
    const mapRef = useRef();

    useImperativeHandle(ref, () => ({
        fitBounds: (bounds, options) => {
            if (mapRef.current) {
                mapRef.current.fitBounds(bounds, options);
            }
        }
    }));

    const activePhoto = useMemo(() =>
        trip?.photos.find(p => p.id === activePhotoId),
        [trip, activePhotoId]);

    // Filter photos with location data
    const mapPhotos = useMemo(() =>
        trip?.photos.filter(p => p.lat !== null && p.lng !== null) || [],
        [trip]);

    // Create GeoJSON points for Supercluster
    const points = useMemo(() => mapPhotos.map(p => ({
        type: 'Feature',
        properties: { cluster: false, photoId: p.id, ...p },
        geometry: {
            type: 'Point',
            coordinates: [p.lng, p.lat]
        }
    })), [mapPhotos]);

    // Initialize Supercluster
    const supercluster = useMemo(() => {
        const sc = new Supercluster({
            radius: 40,
            maxZoom: 16
        });
        sc.load(points);
        return sc;
    }, [points]);

    // Update clusters when view state changes
    useEffect(() => {
        if (mapRef.current && supercluster) {
            const bounds = mapRef.current.getBounds().toArray().flat();
            setClusters(supercluster.getClusters(bounds, viewState.zoom));
        }
    }, [viewState, supercluster]);

    // Create GeoJSON for radius circles
    const radiusGeoJSON = useMemo(() => ({
        type: 'FeatureCollection',
        features: mapPhotos.map(p => createGeoJSONCircle([p.lng, p.lat], 0.5)) // 0.5km radius
    }), [mapPhotos]);

    // Update view state when trip changes
    useEffect(() => {
        if (trip?.center) {
            setViewState(prev => ({
                ...prev,
                longitude: trip.center[0],
                latitude: trip.center[1],
                zoom: 12,
                transitionDuration: 1000
            }));
        }
    }, [trip]);

    // Update view state when active photo changes
    useEffect(() => {
        if (activePhoto && activePhoto.lat && activePhoto.lng) {
            setViewState(prev => ({
                ...prev,
                longitude: activePhoto.lng,
                latitude: activePhoto.lat,
                zoom: 13,
                transitionDuration: 1000
            }));
        }
    }, [activePhoto]);

    return (
        <Map
            ref={mapRef}
            {...viewState}
            onMove={evt => setViewState(evt.viewState)}
            style={{ width: '100%', height: '100%' }}
            mapStyle="https://tiles.openfreemap.org/styles/bright"
            attributionControl={false}
        >
            <NavigationControl position="top-right" />

            {/* Radius Circles Layer */}
            <Source id="radius-data" type="geojson" data={radiusGeoJSON}>
                <Layer
                    id="radius-layer"
                    type="fill"
                    paint={{
                        'fill-color': '#4ECDC4',
                        'fill-opacity': 0.2
                    }}
                />
            </Source>

            {clusters.map(cluster => {
                const [longitude, latitude] = cluster.geometry.coordinates;
                const { cluster: isCluster, point_count: pointCount } = cluster.properties;

                if (isCluster) {
                    // Get the first photo in the cluster to use as thumbnail
                    const leaves = supercluster.getLeaves(cluster.id, 1);
                    const firstPhoto = leaves[0].properties;

                    return (
                        <Marker
                            key={`cluster-${cluster.id}`}
                            longitude={longitude}
                            latitude={latitude}
                            anchor="center"
                            onClick={(e) => {
                                e.originalEvent.stopPropagation();
                                const expansionZoom = Math.min(
                                    supercluster.getClusterExpansionZoom(cluster.id),
                                    20
                                );
                                setViewState({
                                    ...viewState,
                                    longitude,
                                    latitude,
                                    zoom: expansionZoom,
                                    transitionDuration: 500
                                });
                            }}
                        >
                            <div className="cluster-marker">
                                <div className="cluster-stack-1" />
                                <div className="cluster-stack-2" />
                                <div className="cluster-content">
                                    <img
                                        src={firstPhoto.src}
                                        alt="cluster thumbnail"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                    <div className="cluster-count">{pointCount}</div>
                                </div>
                            </div>
                        </Marker>
                    );
                }

                const photo = cluster.properties;
                return (
                    <Marker
                        key={`photo-${photo.id}`}
                        longitude={longitude}
                        latitude={latitude}
                        anchor="bottom"
                        onClick={(e) => {
                            e.originalEvent.stopPropagation();
                            onMarkerClick(photo.id);
                            const element = document.getElementById(`photo-${photo.id}`);
                            if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                    >
                        <div
                            className={`custom-marker ${activePhotoId === photo.id ? 'active' : ''}`}
                            style={{
                                '--pin-color': activePhotoId === photo.id ? '#FF6B6B' : '#4ECDC4'
                            }}
                        >
                            <div className="pin-wrapper">
                                <div className="pin-emoji" style={{ padding: 0, overflow: 'hidden' }}>
                                    <img
                                        src={photo.src}
                                        alt="location"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div className="pin-tip" />
                            </div>
                        </div>
                    </Marker>
                );
            })}
        </Map>
    );
});

export default TripMap;
